import {
    ShoppingCart,
    Plus,
    Minus,
    Trash2,
    Search,
    X,
    Info,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";

import { useLocationContext } from "../../contexts/locationContext";
import { unwrapApiArray } from "../../shared/utils/apiResponse";
import { handleGetShop } from "../../userDashboard/userControllers/shopController";
import { formatItemPrice } from "../../utils/price";

import Cart from "./Cart";
import Thankyou from "./Thankyou";



interface Product {
    id: number;
    name: string;
    price: string | number;
    description?: string;
    media?: {
        file_path: string;
    }[];
    [key: string]: unknown;
}

interface ProductInfoModalProps {
    product: Product | null;
    isOpen: boolean;
    onClose: () => void;
    cart: Record<string, number>;
    addToCart: (productId: number) => void;
    decreaseQuantity: (productId: number) => void;
    removeFromCart: (productId: number) => void;
}

const ProductInfoModal: React.FC<ProductInfoModalProps> = ({
    product,
    isOpen,
    onClose,
    cart,
    addToCart,
    decreaseQuantity,
    removeFromCart,
}) => {
    const { location: storedLocation } = useLocationContext();

    if (!isOpen || !product) return null;

    const getProductImage = (product: Product) => {
        if (product.media && product.media.length > 0) {
            return product.media[0]?.file_path ?? null;
        }
        return null;
    };

    const renderDescription = (description?: string) => {
        if (!description)
            return <p className="text-gray-500 ">No description available.</p>;

        return (
            <div
                className="prose prose-sm max-w-none text-gray-700 text-sm md:text-base font-Manrope"
                dangerouslySetInnerHTML={{ __html: description }}
            />
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center px-4 justify-center">
            <div className="bg-white rounded-lg max-w-2xl w-full ">
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className=" text-base md:text-lg font-semibold text-gray-900">
                        Product Details
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="p-4 space-y-4 max-h-[75vh] overflow-y-auto">
                    <div className="w-full h-64 bg-gray-100 rounded-lg overflow-hidden">
                        {getProductImage(product) ? (
                            <img
                                src={getProductImage(product) || ""}
                                alt={product.name}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <ShoppingCart size={48} />
                            </div>
                        )}
                    </div>

                    <div className="space-y-3">
                        <h3 className=" text-base md:text-xl font-semibold text-gray-900 font-Manrope">
                            {product.name}
                        </h3>
                        <p className=" text-lg md:text-2xl font-bold text-[#317333] font-Manrope">
                            {formatItemPrice(product, (storedLocation as Record<string, string>).countryCode)}
                        </p >

                        < div className="border-t pt-3" >
                            <h4 className="font-medium text-gray-900 font-Manrope mb-2">
                                Description
                            </h4>
                            {renderDescription(product.description)}
                        </div >
                    </div >
                </div >

                < div className="border-t p-4 w-full" >
                    {
                        cart[product.id] ? (
                            <div className="flex items-center justify-between" >
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => decreaseQuantity(product.id)}
                                        className="border border-[#BABABA] p-2 rounded-lg hover:bg-gray-50"
                                    >
                                        <Minus size={16} />
                                    </button>
                                    <span className="px-4 py-2 bg-gray-50 rounded-lg font-medium">
                                        {cart[product.id]}
                                    </span>
                                    <button
                                        onClick={() => addToCart(product.id)}
                                        className="border border-[#BABABA] p-2 rounded-lg hover:bg-gray-50"
                                    >
                                        <Plus size={16} />
                                    </button>
                                </div>
                                <button
                                    onClick={() => removeFromCart(product.id)}
                                    className="text-gray-400 hover:text-red-500 p-2"
                                >
                                    <Trash2 size={20} />
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => addToCart(product.id)}
                                className="w-full bg-[#317333] text-white font-Manrope text-sm md:text-base font-medium py-3 rounded-lg hover:bg-green-700 transition-colors"
                            >
                                Add to Cart
                            </button>
                        )}
                </div >
            </div >
        </div >
    );
};

const LoadingGrid: React.FC = () => {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 w-full">
            {Array.from({ length: 6 }).map((_, index) => (
                <div key={`skeleton-${index}`} className="bg-gray-100 rounded-lg p-4 animate-pulse">
                    <div className="w-full h-36 sm:h-48 bg-gray-200 rounded-md mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded mb-4 w-1/4"></div>
                    <div className="h-10 bg-gray-200 rounded"></div>
                </div>
            ))}
        </div>
    );
};

export default function ProductGrid({ where }: Readonly<{ where: "landing" | "dashboard" }>) {
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState<Product[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
    const [cart, setCart] = useState<Record<string, number>>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [showCart, setShowCart] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const itemsPerPage = 6;
    const [showThankYouModal, setShowThankYouModal] = useState(false);

    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [showProductModal, setShowProductModal] = useState(false);
    const { location: storedLocation } = useLocationContext();

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const params: Record<string, string> = {};
            const loc = storedLocation as Record<string, string>;
            if (loc?.countryCode) {
                params.country_code = loc.countryCode;
            }
            if (loc?.state) {
                params.state = loc.state;
            }
            if (loc?.city) {
                params.city = loc.city;
            }

            const data = await handleGetShop<Product[]>(params);
            const list = unwrapApiArray<Product>(data);
            setProducts(list);
            setFilteredProducts(list);
        } catch (error) {
            console.error("Error fetching shop:", error);
        } finally {
            setLoading(false);
        }
    }, [storedLocation]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    useEffect(() => {
        const savedCart = localStorage.getItem("cart");
        if (savedCart) {
            try {
                setCart(JSON.parse(savedCart));
            } catch (e) {
                console.error("Error parsing cart from localStorage", e);
            }
        }
    }, []);

    useEffect(() => {
        localStorage.setItem("cart", JSON.stringify(cart));
    }, [cart]);

    useEffect(() => {
        if (searchQuery.trim() === "") {
            setFilteredProducts(products);
        } else {
            const query = searchQuery.toLowerCase();
            const filtered = products.filter((product) =>
                product.name.toLowerCase().includes(query)
            );
            setFilteredProducts(filtered);
        }
        setCurrentPage(1);
    }, [searchQuery, products]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredProducts.slice(
        indexOfFirstItem,
        indexOfLastItem
    );
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

    const addToCart = (productId: number) => {
        setCart((prev) => ({
            ...prev,
            [productId]: (prev[productId] || 0) + 1,
        }));
    };

    const decreaseQuantity = (productId: number) => {
        setCart((prev) => {
            const newCart = { ...prev };
            const currentQty = newCart[productId] ?? 0;
            if (currentQty > 1) {
                newCart[productId] = currentQty - 1;
            } else {
                delete newCart[productId];
            }
            return newCart;
        });
    };

    const removeFromCart = (productId: number) => {
        setCart((prev) => {
            const newCart = { ...prev };
            delete newCart[productId];
            return newCart;
        });
    };

    const clearCart = () => {
        setCart({});
        localStorage.removeItem("cart");
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const clearSearch = () => {
        setSearchQuery("");
    };

    const getProductImage = (product: Product) => {
        if (product.media && product.media.length > 0) {
            return product.media[0]?.file_path ?? "";
        }
        return "";
    };

    const getCartItemCount = () => {
        return Object.values(cart).reduce((sum, quantity) => sum + quantity, 0);
    };

    const openProductModal = (product: Product) => {
        setSelectedProduct(product);
        setShowProductModal(true);
    };

    const closeProductModal = () => {
        setSelectedProduct(null);
        setShowProductModal(false);
    };

    const renderContent = () => {
        if (loading) {
            return <LoadingGrid />;
        }

        if (filteredProducts.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-12 sm:py-16 text-gray-500">
                    {searchQuery ? (
                        <>
                            <Search size={36} className="mb-4" />
                            <p className="text-lg sm:text-xl">
                                No products match your search
                            </p>
                            <button
                                onClick={clearSearch}
                                className="mt-4 text-[#317333] hover:text-green-700"
                            >
                                Clear search
                            </button>
                        </>
                    ) : (
                        <>
                            <ShoppingCart size={36} className="mb-4" />
                            <p className="text-lg sm:text-xl">No products available</p>
                            <p className="text-sm text-gray-400">
                                We're working on it! Come back soon.
                            </p>
                        </>
                    )}
                </div>
            );
        }

        return (
            <div
                className={`grid grid-cols-1  ${showCart ? "sm:grid-cols-1" : "sm:grid-cols-2"
                    } lg:grid-cols-3 gap-4 sm:gap-6`}
            >
                {currentItems.map((product) => (
                    <div
                        key={product.id}
                        className="border border-[#EAEBF0] rounded-[15px] p-3 sm:p-4"
                    >
                        <div className="relative mb-3 sm:mb-4">
                            <button
                                type="button"
                                onClick={() => openProductModal(product)}
                                className="w-full focus:outline-none focus:ring-2 focus:ring-green-500 rounded-md overflow-hidden"
                                aria-label={`View details for ${product.name}`}
                            >
                                <img
                                    src={getProductImage(product)}
                                    alt=""
                                    className="w-full h-36 sm:h-48 object-cover hover:scale-105 transition-transform duration-300"
                                />
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={() => openProductModal(product)}
                            className="w-full flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-green-500 rounded-lg p-1 -m-1"
                        >
                            <h3 className="text-black font-normal text-sm sm:text-base mb-1 sm:mb-2 truncate flex-grow">
                                {product.name}
                            </h3>
                            <Info
                                className="w-4 shrink-0 hover:text-[#317333] transition-colors"
                            />
                        </button>
                        <p className="text-black font-bold text-xs sm:text-sm mb-3 sm:mb-4">
                            {formatItemPrice(product, (storedLocation as Record<string, string>).countryCode)}
                        </p >

                        {
                            cart[product.id] ? (
                                <div className="flex items-center justify-between" >
                                    <div className="flex items-center">
                                        <button
                                            onClick={() => decreaseQuantity(product.id)}
                                            className="border border-[#BABABA] p-1 sm:p-2 rounded-[10px]"
                                        >
                                            <Minus size={14} />
                                        </button>
                                        <span className="bg-white px-2 sm:px-4 py-1 sm:py-2">
                                            {cart[product.id]}
                                        </span>
                                        <button
                                            onClick={() => addToCart(product.id)}
                                            className="border border-[#BABABA] p-1 sm:p-2 rounded-[10px]"
                                        >
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => removeFromCart(product.id)}
                                        className="text-gray-400 hover:text-red-500"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => addToCart(product.id)}
                                    className="w-full bg-[#317333] text-white font-medium text-sm sm:text-base py-2 rounded-md hover:bg-green-700"
                                >
                                    Add to Cart
                                </button>
                            )
                        }
                    </div >
                ))}
            </div >
        );
    };

    return (
        <>
            <div className="font-Manrope w-full relative">
                <div
                    className={`transition-all duration-300 ${showCart ? "sm:mr-96" : ""
                        }`}
                >
                    <div className="flex flex-col sm:flex-row justify-between items-center mb-6 sm:mb-8 gap-3 sm:gap-4">
                        <h1 className="text-xl sm:text-2xl font-bold text-black self-start sm:self-auto">
                            Shop
                        </h1>

                        <div className="flex w-full sm:w-auto gap-2 sm:gap-4">
                            <div className="relative flex-grow">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search size={16} className="text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search products..."
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={clearSearch}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                    >
                                        <X size={16} />
                                    </button>
                                )}
                            </div>

                            <button
                                onClick={() => setShowCart(!showCart)}
                                className={`flex items-center px-3 sm:px-4 py-2 rounded-full whitespace-nowrap ${showCart
                                    ? "bg-green-100 text-green-700"
                                    : "bg-gray-100 hover:bg-gray-200"
                                    }`}
                            >
                                <ShoppingCart className="mr-1 sm:mr-2" size={16} />
                                <span className="text-xs sm:text-sm">
                                    {getCartItemCount()} items
                                </span>
                            </button>
                        </div>
                    </div>

                    {renderContent()}
                </div>
            </div>

            {
                !loading && filteredProducts.length > itemsPerPage && (
                    <div className="flex justify-center items-center mt-6 sm:mt-8">
                        <div className="flex items-center gap-2 sm:gap-4">
                            <button
                                type="button"
                                onClick={() =>
                                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                                }
                                disabled={currentPage === 1}
                                className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded ${currentPage === 1
                                    ? "bg-gray-100 text-gray-400"
                                    : "bg-gray-200 hover:bg-gray-300"
                                    }`}
                            >
                                Prev
                            </button>

                            <span className="text-gray-500 text-xs sm:text-sm">
                                Page {currentPage} of {totalPages}
                            </span>

                            <button
                                type="button"
                                onClick={() =>
                                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                                }
                                disabled={currentPage === totalPages}
                                className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded ${currentPage === totalPages
                                    ? "bg-gray-100 text-gray-400"
                                    : "bg-gray-200 hover:bg-gray-300"
                                    }`}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )
            }


            <Cart
                showCart={showCart}
                setShowCart={setShowCart}
                cart={cart}
                products={products}
                addToCart={addToCart}
                decreaseQuantity={decreaseQuantity}
                removeFromCart={removeFromCart}
                where={where}
                clearCart={clearCart}
                setShowThankYouModal={setShowThankYouModal}
                setCart={setCart}
            />

            <ProductInfoModal
                product={selectedProduct}
                isOpen={showProductModal}
                onClose={closeProductModal}
                cart={cart}
                addToCart={addToCart}
                decreaseQuantity={decreaseQuantity}
                removeFromCart={removeFromCart}
            />

            {
                showThankYouModal && (
                    <Thankyou onClose={() => setShowThankYouModal(false)} />
                )
            }
        </>
    );
}
